import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input,
  OnChanges, OnDestroy, OnInit, Output, QueryList, Renderer2, SimpleChanges, ViewChild, ViewChildren
} from '@angular/core';
import { EventsService, IconsOld, NpaIconOld, NpaId, NpaStatuses, ToolService } from '@circe/core';
import { NpaActionConfig } from './table-actions/table-actions.types';
import { BehaviorSubject, fromEvent, isObservable, Observable, of, Subject, Subscription } from 'rxjs';
import { TableService } from './table.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { debounceTime, delay, takeUntil, tap } from 'rxjs/operators';
import { SelectComponent } from '@circe/select';
import { _isTableSource, TableSource } from './table-source';
import { TableActionsComponent } from './table-actions/table-actions.component';
import { NpaTableConfiguration } from './configurations/table.configuration';
import { NpaTableActionsConfiguration } from './configurations/table-actions.configuration';
import {
  NpaBadgeColumnConfig, NpaIconColumnConfig, NpaLinkColumnConfig, NpaSelectColumnConfig, NpaTableCheckboxSelection,
  NpaTableColumn, NpaTableColumnConfigurationModel, NpaTableConfigModel, NpaTableIconSource,
  NpaTableRowValue, NpaTableSort, NpaTableSortType, NpaTableStatusSource
} from './table.types';

@Component({
  selector: 'npa-table',
  templateUrl: 'table.component.html',
  styleUrls: ['table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableComponent<T> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('table') _table: ElementRef<HTMLElement>;
  @ViewChild('tableBody') _tableBody: ElementRef<HTMLElement>;
  @ViewChild('tableRow') _tableRow: ElementRef<HTMLElement>;
  @ViewChild('tableHeader') _tableHeader: ElementRef<HTMLElement>;
  @ViewChild(CdkVirtualScrollViewport) _viewPort: CdkVirtualScrollViewport;
  @ViewChildren('tableActions') tableActions: QueryList<TableActionsComponent<T>>;
  @ViewChildren(SelectComponent) selects: QueryList<SelectComponent>;

  @Input() id: string;
  @Input() config: NpaTableConfigModel<T>;
  @Input() data: T[] | Observable<T[]> | TableSource<T>;
  @Input() showSpinner: boolean;
  @Input() showLoadOnScrollSpinner: boolean;
  @Input() hotRender: boolean;
  @Input()
  get sort(): NpaTableSort | BehaviorSubject<NpaTableSort> {
    return this.sort$.value;
  }

  set sort(sort: NpaTableSort | BehaviorSubject<NpaTableSort>) {
    this._sortSubscription?.unsubscribe();
    this._sortSubscription = isObservable(sort) ? sort.subscribe(this.sort$) : of(sort).subscribe(this.sort$);
  }

  @Output() internalUpdate: EventEmitter<NpaTableRowValue<T>[]> = new EventEmitter<NpaTableRowValue<T>[]>();
  @Output() sortChange: EventEmitter<NpaTableSort> = new EventEmitter<NpaTableSort>();
  @Output() scrolledToBottom: EventEmitter<void> = new EventEmitter<void>();

  public internalId: string = ToolService.generateUuid();
  public dataSource$: Observable<any[]>;
  public dataSourceSubscription: Subscription;
  public rows$: BehaviorSubject<NpaTableColumn<T>[][]> = new BehaviorSubject<NpaTableColumn<T>[][]>([]);
  public tableConfiguration: NpaTableConfiguration<T>;
  public tableActionsConfiguration: NpaTableActionsConfiguration<T>;
  public checkboxColumnsModel: Record<NpaId, NpaTableCheckboxSelection> = {};

  public rowsHoverState: boolean[] = [];
  public defaultAction: NpaActionConfig<T>;
  public groupActions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showInternalSpinner$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public manualActionsHide$: Subject<void> = new Subject<void>();
  public sort$: BehaviorSubject<NpaTableSort> = new BehaviorSubject<NpaTableSort>(null);

  private _originalDataCopy: T[] = [];
  private _sortSubscription: Subscription;
  private _scrollSubscription: Subscription;
  private _iconHandler: IconsOld;
  private _componentDestroyed$: Subject<void> = new Subject<void>();

  @HostListener('window:resize') onResize(): void {
    this.showInternalSpinner$.next(true);
  }

  constructor(
    public ev: EventsService,
    public tools: ToolService,
    public tableService: TableService,
    private _renderer: Renderer2
  ) {
    this._iconHandler = new IconsOld();
  }

  private _setDataStream(): void {
    if (this.data) {
      if (Array.isArray(this.data)) {
        this.dataSource$ = of(this.data as any[]);
      } else if (isObservable(this.data)) {
        this.dataSource$ = this.data;
      } else if (_isTableSource(this.data)) {
        this.dataSource$ = this.data.connect();
      } else {
        throw new Error('[TableComponent]: data input must be of type T[], Observable<T[]> or TableSource<T>');
      }
      this.dataSourceSubscription?.unsubscribe();
      this.dataSourceSubscription = this.dataSource$.pipe(takeUntil(this._componentDestroyed$)).subscribe((data: any[]) => {
        this._originalDataCopy = [...data];
        this.rows$.next(this._processRows(data, this.tableConfiguration?.columns));
      });
      this.rows$.pipe(
        takeUntil(this._componentDestroyed$),
        delay(1),
        tap((rows: NpaTableColumn<T>[][]) => {
          this._setScrollListener();
          this._viewPort.checkViewportSize();
        })
      ).subscribe();
    }
  }

  private _processRows(rows: any[], columnsConfig: NpaTableColumnConfigurationModel<T>[]): NpaTableColumn<T>[][] {
    return columnsConfig ? rows.map((row: any) => {
      return columnsConfig.map((c: NpaTableColumnConfigurationModel<T>) => this._processColumn(row, c));
    }) : [];
  }

  private _processColumn(rowData: any, columnConfig: NpaTableColumnConfigurationModel<T>): NpaTableColumn<T> {
    const column: NpaTableColumn<T> = { ...columnConfig };
    let auxColumn: NpaTableColumn<T> = { sortable: true, ...columnConfig };

    switch (column.type) {
      case 'checkbox':
        auxColumn = this._processCheckboxColumn(column, rowData);
        break;
      case 'radio':
        auxColumn = this._processRadioColumn(column);
        break;
      case 'select':
        auxColumn = this._processSelectColumn(column, rowData);
        break;
      case 'icon':
        auxColumn = this._processIconColumn(rowData, column);
        break;
      case 'badge':
        auxColumn = this._processBadgeColumn(rowData, column);
        break;
      case 'date':
        auxColumn = this._processDateColumn(rowData, column);
        break;
      default:
        if ('param' in columnConfig) {
          auxColumn.value = ToolService.getValueFromMultiLevelObject(rowData, columnConfig.param);
        }
    }

    return auxColumn;
  }

  private _processCheckboxColumn(columnConfig: NpaTableColumnConfigurationModel<T>, rowData: any): NpaTableColumn<T> {
    const _rowId: NpaId = ToolService.getValueFromMultiLevelObject(rowData, this.tableConfiguration.keyColumnParam);
    const _checkboxColumn: NpaTableColumn<T> = { ...columnConfig };
    const _tableServiceData = this.tableService.tables[this.id];
    const _columnConfigData = _tableServiceData ? _tableServiceData[columnConfig.id] : null;
    _checkboxColumn.value = !!_columnConfigData && _columnConfigData.includes(_rowId);

    return _checkboxColumn;
  }

  private _processRadioColumn(columnConfig: NpaTableColumnConfigurationModel<T>): NpaTableColumn<T> {
    const _radioColumn: NpaTableColumn<T> = { ...columnConfig };
    const _tableServiceData = this.tableService.tables[this.id];
    const _columnConfigData = _tableServiceData ? _tableServiceData[columnConfig.id] : null;
    _radioColumn.value = _columnConfigData ?? -1;

    return _radioColumn;
  }

  private _processSelectColumn(columnConfig: NpaTableColumnConfigurationModel<T>, rowData: any): NpaTableColumn<T> {
    const _rowId: NpaId = ToolService.getValueFromMultiLevelObject(rowData, this.tableConfiguration.keyColumnParam);
    const _columnConfigIsSet: boolean = !!columnConfig.typeConfig;
    const _selectColumn: NpaTableColumn<T> = { ...columnConfig };
    const _typeConfig: NpaSelectColumnConfig = _selectColumn.typeConfig as NpaSelectColumnConfig;
    const _tableServiceData = this.tableService.tables[this.id];
    const _columnConfigData = _tableServiceData ? _tableServiceData[columnConfig.id] : null;

    _selectColumn.selectConfig = {
      dropdownRelativeElement: 'npa-table__viewport',
      ..._typeConfig.selectConfig,
      size: 'small',
      setDropdownParentTo: { type: 'id', name: 'npa-table-' + this.internalId }
    };

    if (_columnConfigIsSet) {
      if ('defaultValue' in _typeConfig) {
        _selectColumn.value = _typeConfig.defaultValue;
      }
      if ('multilevel' in _typeConfig) {
        _selectColumn.selectConfig = {
          ..._selectColumn.selectConfig,
          multiple: _typeConfig.multilevel,
          multipleSelectionBehavior: 'checkbox'
        };
        _selectColumn.value = _columnConfigData ? _columnConfigData[_rowId] : [];
      } else {
        _selectColumn.value = _columnConfigData ? _columnConfigData[_rowId] : '';
      }
      if ('placeholder' in _typeConfig) {
        _selectColumn.selectConfig = {
          ..._selectColumn.selectConfig,
          placeholder: _typeConfig.placeholder
        };
      }
      if (_selectColumn.selectConfig.multiple && 'multilevelButtonLabel' in _typeConfig) {
        _selectColumn.selectConfig = {
          ..._selectColumn.selectConfig,
          confirmButtonLabel: _typeConfig.multilevelButtonLabel
        };
      }
    }

    return _selectColumn;
  }

  private _processIconColumn(rowData: any, columnConfig: NpaTableColumnConfigurationModel<T>): NpaTableColumn<T> {
    const _columnConfigIsSet: boolean = !!columnConfig.typeConfig;
    const _selectColumn: NpaTableColumn<T> = { ...columnConfig };
    const _typeConfig: NpaIconColumnConfig<T> = _selectColumn.typeConfig as NpaIconColumnConfig<T>;

    if (_columnConfigIsSet) {
      if ('icon' in _typeConfig) {
        _selectColumn.icon = this._getIcon(rowData, columnConfig);
      }

      if ('onlyIcon' in _typeConfig) {
        _selectColumn.onlyIcon = _typeConfig.onlyIcon;
      }

      if ('param' in columnConfig) {
        _selectColumn.value = ToolService.getValueFromMultiLevelObject(rowData, columnConfig.param);
      }
    }

    return _selectColumn;
  }

  private _processBadgeColumn(rowData: any, columnConfig: NpaTableColumnConfigurationModel<T>): NpaTableColumn<T> {
    const _columnConfigIsSet: boolean = !!columnConfig.typeConfig;
    const _badgeColumn: NpaTableColumn<T> = { ...columnConfig };
    const _typeConfig: NpaBadgeColumnConfig<T> = _badgeColumn.typeConfig as NpaBadgeColumnConfig<T>;

    if (_columnConfigIsSet) {
      if ('type' in _typeConfig) {
        _badgeColumn.badgeType = this._getBadgeType(rowData, columnConfig);
      }

      if ('param' in columnConfig) {
        _badgeColumn.value = ToolService.getValueFromMultiLevelObject(rowData, columnConfig.param);
      }
    }

    return _badgeColumn;
  }

  private _processDateColumn(rowData: any, columnConfig: NpaTableColumnConfigurationModel<T>): NpaTableColumn<T> {
    const _columnConfigIsSet: boolean = !!columnConfig.typeConfig;
    const _dateColumn: NpaTableColumn<T> = { ...columnConfig };

    if (_columnConfigIsSet) {
      if ('param' in columnConfig) {
        _dateColumn.value = new Date(ToolService.getValueFromMultiLevelObject(rowData, columnConfig.param));
      }
    }

    return _dateColumn;
  }

  private _getBadgeType(row: any, column: NpaTableColumnConfigurationModel<T>): string {
    const _typeConfig: NpaBadgeColumnConfig<T> = column.typeConfig as NpaBadgeColumnConfig<T>;
    const _badgeType: NpaStatuses | NpaTableStatusSource<T> = _typeConfig.type;
    if (_badgeType) {
      return (typeof _badgeType === 'function' ? (_badgeType as NpaTableStatusSource<T>)(row) : _badgeType) as string;
    }
    return '';
  }

  private _getIcon(row: any, column: NpaTableColumnConfigurationModel<T>): string {
    let auxIcon: NpaIconOld = { classes: '' };
    const _typeConfig: NpaIconColumnConfig<T> = column.typeConfig as NpaIconColumnConfig<T>;
    const _icon: NpaIconOld | NpaTableIconSource<T> = _typeConfig.icon;
    if (_icon) {
      auxIcon = (typeof _icon === 'function' ? (_icon as NpaTableIconSource<T>)(row) : _icon) as NpaIconOld;
    }
    return this._iconHandler.getIcon(auxIcon);
  }

  private _getTableValue(): NpaTableRowValue<T>[] {
    return this.rows$.value.map(this.getRowValue.bind(this));
  }

  private _hideAllDropdowns(): void {
    this.manualActionsHide$.next();
    if (this.selects.length) {
      for (const _select of this.selects) {
        if (_select.showDropdown) {
          _select.setDropdown();
        }
      }
    }
  }

  private _setScrollListener(): void {
    if (!!this._viewPort) {
      this._scrollSubscription?.unsubscribe();
      this._scrollSubscription = fromEvent(this._viewPort.getElementRef().nativeElement, 'scroll')
        .pipe(
          tap(() => this._hideAllDropdowns()),
          debounceTime(300),
          takeUntil(this._componentDestroyed$)
        ).subscribe(this.checkScroll.bind(this));
    }
  }

  private _setResizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(
        tap(() => this.manualActionsHide$.next()),
        debounceTime(300),
        takeUntil(this._componentDestroyed$)
      )
      .subscribe(() => {
        this.showInternalSpinner$.next(false);
      });

    this.showInternalSpinner$.pipe(takeUntil(this._componentDestroyed$), delay(1))
      .subscribe(() => {
        if (this._viewPort) {
          this._viewPort.checkViewportSize();
        }
      });
  }

  private _processConfiguration(config: NpaTableConfigModel<T>): void {
    this.tableConfiguration = new NpaTableConfiguration<T>(this.rows$, config);
    this.tableActionsConfiguration = new NpaTableActionsConfiguration<T>(this.tableConfiguration.actionsConfigurationModel);
    this._checkBiggerTableActions(this.tableActionsConfiguration.group);
  }

  public ngOnInit(): void {
    this._processConfiguration(this.config);
    this._setDataStream();
  }

  public ngAfterViewInit(): void {
    this._setResizeListener();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.hotRender && (changes.config?.currentValue && changes.config?.previousValue)) {
      this._processConfiguration(changes.config?.currentValue);
      this._setDataStream();
    }

    if (changes.data?.currentValue) {
      this._setDataStream();
      this._checkBiggerTableActions(this.groupActions$.value);
    }
  }

  public checkColumnCheckboxes(columnId: NpaId): void {
    const _rows: NpaTableColumn<T>[][] = [...this.rows$.value];
    const columnValues: boolean[] = _rows.map((r: NpaTableColumn<T>[]) => !!(r.find((c: NpaTableColumn<T>) => c.id === columnId)?.value));
    if (!this.checkboxColumnsModel[columnId]) {
      this.checkboxColumnsModel[columnId] = {
        checkAll: false,
        checkNone: true,
        model: false
      };
    }
    this.checkboxColumnsModel[columnId].checkAll = !!_rows.length && columnValues && columnValues?.every((v: boolean) => v);
    this.checkboxColumnsModel[columnId].checkNone = columnValues?.every((v: boolean) => !v);
    this.checkboxColumnsModel[columnId].model = this.checkboxColumnsModel[columnId].checkAll || !this.checkboxColumnsModel[columnId].checkNone;

  }

  public sortTableRows(column: NpaTableColumnConfigurationModel<T>): void {
    if (this.tableConfiguration.sortTable && column.sortable && column.columnName) {
      const { sortType, columnId } = this.sort$.value;
      const _newSortType: NpaTableSortType = (sortType === 'desc' || columnId !== column.id) ? 'asc' : 'desc';

      this.sort$.next({ columnId: column.id, sortType: _newSortType });
      this.sortChange.emit({ columnId: column.id, sortType: _newSortType });
    }
  }

  public updateRadioColumn(columnId: NpaId, newValue: number): void {
    const _rows: NpaTableColumn<T>[][] = [...this.rows$.value];
    _rows.forEach((row: NpaTableColumn<T>[], rowIndex: number) => {
      const _column: NpaTableColumn<T> = row.find((r: NpaTableColumn<T>) => r.id === columnId);
      _column.value = newValue;

      if (_column.rowId === newValue) {
        const _row: NpaTableRowValue<T> = this.getRowValue(row, rowIndex);
        const _rowId: NpaId = ToolService.getValueFromMultiLevelObject(_row.npaTableDataSource, this.tableConfiguration.keyColumnParam);
        this.tableService
          .setRadioData(this.id, _rowId, _column.id);
      }
    });
    this.internalUpdate.emit(this._getTableValue());
  }

  public updateCheckboxColumn(column: NpaTableColumn<T>, row: NpaTableColumn<T>[], rowIndex: number, newValue: any, bypassCheck?: boolean): void {
    column.value = newValue;
    if (!this.checkboxColumnsModel[column.id]) {
      this.checkboxColumnsModel[column.id] = {
        checkAll: false,
        checkNone: true,
        model: false
      };
    }

    const _rows: NpaTableColumn<T>[][] = [...this.rows$.value];
    const _row: NpaTableRowValue<T> = this.getRowValue(row, rowIndex);
    const _rowId: NpaId = ToolService.getValueFromMultiLevelObject(_row.npaTableDataSource, this.tableConfiguration.keyColumnParam);
    const columnValues: boolean[] = _rows.map((r: NpaTableColumn<T>[]) => !!r.find((c: NpaTableColumn<T>) => c.id === column.id).value);

    this.checkboxColumnsModel[column.id].checkAll = columnValues.every((v: boolean) => v);
    this.checkboxColumnsModel[column.id].checkNone = columnValues.every((v: boolean) => !v);
    // this.checkboxColumnsModel[column.id].model = this.checkboxColumnsModel[column.id].checkAll || !this.checkboxColumnsModel[column.id].checkNone;

    if (newValue) {
      this.tableService.addCheckboxData(this.id, _rowId, column.id);
    } else {
      this.tableService.deleteCheckboxData(this.id, _rowId, column.id);
    }

    if (!bypassCheck) {
      this.checkColumnCheckboxes(column.id);
    }
    this.internalUpdate.emit(this._getTableValue());
  }

  public updateColumnValue(column: NpaTableColumn<T>, row: NpaTableColumn<T>[], rowIndex: number, newValue: any): void {
    const _row: NpaTableRowValue<T> = this.getRowValue(row, rowIndex);
    const _rowId: NpaId = ToolService.getValueFromMultiLevelObject(_row.npaTableDataSource, this.tableConfiguration.keyColumnParam);
    column.value = newValue;

    this.tableService
      .setSelectData(this.id, _rowId, column.id, newValue);
    this.internalUpdate.emit(this._getTableValue());
  }

  public linkAction(column: NpaTableColumn<T>, row: NpaTableColumn<T>[], rowIndex: number): void {
    const _typeConfig: NpaLinkColumnConfig<T> = column.typeConfig as NpaLinkColumnConfig<T>;
    if (_typeConfig?.linkAction) {
      _typeConfig.linkAction(this.getRowValue(row, rowIndex));
    }
  }

  public setColumnCheckboxes(columnId: NpaId): void {
    const _rows: NpaTableColumn<T>[][] = [...this.rows$.value];
    this.checkboxColumnsModel[columnId].model = !this.checkboxColumnsModel[columnId].model;

    _rows.forEach((row: NpaTableColumn<T>[], rowIndex: number) => {
      const _column: NpaTableColumn<T> = row.find((c: NpaTableColumn<T>) => c.id === columnId);
      this.updateCheckboxColumn(_column, row, rowIndex, this.checkboxColumnsModel[columnId].model, true);
    });

    this.checkColumnCheckboxes(columnId);
    this.internalUpdate.emit(this._getTableValue());
  }

  public rowHover(columnIndex: number): void {
    this.rowsHoverState = new Array(this.rowsHoverState.length).fill(false);
    this.rowsHoverState[columnIndex] = true;
  }

  public tableMouseLeave(): void {
    this.rowsHoverState = new Array(this.rowsHoverState.length).fill(false);
  }

  public rowMouseLeave(rowIndex: number): void {
    this.rowsHoverState = [...this.rowsHoverState.slice(0, rowIndex), false, ...this.rowsHoverState.slice(rowIndex + 1)];
  }

  public checkScroll(): void {
    const _viewPortElement: HTMLElement = this._viewPort.getElementRef().nativeElement;
    const _bodyHasScroll = _viewPortElement.offsetHeight < _viewPortElement.scrollHeight;
    if (this._viewPort && this.tableConfiguration.emitOnScroll) {
      const _viewPort: HTMLElement = this._viewPort.getElementRef().nativeElement as HTMLElement;
      if (_bodyHasScroll && _viewPort.scrollTop + _viewPort.offsetHeight >= _viewPort.scrollHeight) {
        this.scrolledToBottom.emit();
      }
    }
  }

  public getRowValue(row: NpaTableColumn<T>[], rowIndex: number): NpaTableRowValue<T> {
    const _originalRow: any = this._originalDataCopy[rowIndex] ?? {};
    const _rowValue: NpaTableRowValue<T> = {
      npaTableDataSource: { ..._originalRow }
    };
    for (const column of row) {
      if (column.id !== 'npa-table-actions-column') {
        _rowValue[column.id] = column.type === 'radio' ? (column.value === rowIndex) : column.value;
      }
    }
    return { ..._rowValue };
  }

  private _checkBiggerTableActions(group: boolean): void {
    let _biggerTableActions: HTMLElement;
    const _tableActionsComponents: TableActionsComponent<T>[] = this.tableActions ? Array.from(this.tableActions) : [];

    for (const _tableActionsComponent of _tableActionsComponents) {
      if (!_biggerTableActions || _tableActionsComponent.actionsContainer?.nativeElement.scrollWidth > _biggerTableActions.scrollWidth) {
        _biggerTableActions = _tableActionsComponent.actionsContainer?.nativeElement;
      }
    }

    const _tableActionsComponentParent: HTMLElement = _tableActionsComponents[0]?.el.nativeElement.parentNode as HTMLElement;
    if (!group && _biggerTableActions && _biggerTableActions.scrollWidth >= _tableActionsComponentParent?.offsetWidth) {
      this.groupActions$.next(true);
    } else {
      this.groupActions$.next(group);
    }
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
    this._componentDestroyed$.unsubscribe();
  }
}
